import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  OutlinedInput,
  InputAdornment,
  FormHelperText,
  FormControl,
  Typography,
  Stack,
} from "@mui/material";
import theme from "../../../../../muiTheme";
import "./ManagementsUpdateNewPowersTable.scss";
import { useState } from "react";
import { SuppliesDetailObjectPower } from "../../../../../core/models/suppliesDetail/suppliesDetail.model";
import {
  NewPowersBlur,
  NewPowers,
} from "../../../../../core/models/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers";
import { useTranslation } from "react-i18next";
import EmptyStateComponent from "../../../../UI/EmptyStateComponent/EmptyStateComponent";

interface ManagementsUpdateNewPowersTableProps {
  powers: SuppliesDetailObjectPower[];
  newPowers: NewPowers;
  isLoading: boolean;
  setNewPowers: React.Dispatch<React.SetStateAction<NewPowers>>;
  rate: string
}

const ManagementsUpdateNewPowersTable = ({
  powers,
  newPowers,
  isLoading,
  setNewPowers,
  rate
}: ManagementsUpdateNewPowersTableProps) => {
  const { t } = useTranslation();
  const [newPowersBlur, setNewPowersBlur] = useState<NewPowersBlur>({});

  const updateNewPowersColumns = [
    {
      id: "period",
      label: "Periodo",
      width: "33.3%",
      color: theme.palette.background.default,
      backgroundColor: theme.palette.grey[700],
      renderValue: (indexNumber: number) => `P${indexNumber + 1}`,
      flexBasis: "20%",
    },
    {
      id: "hiredPower",
      label: "Potencia Actual",
      width: "33.3%",
      color: theme.palette.grey[700],
      backgroundColor: theme.palette.grey[100],
      flexBasis: "75%",
    },
    {
      id: "newPower",
      label: "Nueva Potencia",
      width: "33.3%",
      isUpdateField: true,
      flexBasis: "100%",
    },
  ];
  const checkIsDisabled = (indexNumber: number) => {
    const newPowersValues = Object.values(newPowers);
  
    if (rate.includes("2")) {
      if (indexNumber === 1) {
        return false;
      }
      if (indexNumber === 2) {
        return !newPowersValues[0]?.length;
      }
      if (indexNumber > 2) {
        const previousVisibleIndex = indexNumber - 1 === 1 ? 0 : indexNumber - 1;
        const prevPrevVisibleIndex = previousVisibleIndex === 0 ? undefined : previousVisibleIndex - 1;
        if (prevPrevVisibleIndex !== undefined) {
          return (
            +newPowersValues[prevPrevVisibleIndex] > +newPowersValues[previousVisibleIndex] ||
            !newPowersValues[previousVisibleIndex]?.length
          );
        } else {
          return !newPowersValues[previousVisibleIndex]?.length;
        }
      }
    } else {
      if (!newPowersValues.length || indexNumber === 1) {
        return !newPowersValues[indexNumber - 1]?.length;
      }
      if (indexNumber > 1) {
        return (
          +newPowersValues[indexNumber - 2] > +newPowersValues[indexNumber - 1] ||
          !newPowersValues[indexNumber - 1]?.length
        );
      }
    }
  
    return false;
  };

  return (
    <TableContainer>
      <Table>
        <TableHead
          sx={{ display: { xs: "none", md: "block" } }}
          className="NewPowers-TableHead"
        >
          <TableRow
            style={{
              borderBottom: "1px solid",
              borderRadius: 0,
              borderColor: theme.palette.grey[300],
            }}
          >
            {updateNewPowersColumns.map(({ id, label, width }) => (
              <TableCell key={id} align={"left"} width={width}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(`Gestiones.${label}`)}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {!!powers.length && (
          <TableBody className="NewPowers-TableBody">
            {powers.map(({ hiredPower, idHiredPower }, indexNumber) =>
              rate.includes("2") && indexNumber === 1 ? null : (
                <TableRow
                  key={idHiredPower}
                  sx={{
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    gap: { xs: "5%", md: "" },
                    borderBottom: { xs: "1px solid" },
                    borderColor: {
                      xs: theme.palette.grey[300],
                      md: "transparent",
                    },
                    borderRadius: 0,
                  }}
                >
                  <Typography
                    display={{ md: "none" }}
                    component={"td"}
                    variant="subtitle1"
                    fontWeight={600}
                    flexBasis="100%"
                  >
                  </Typography>
                  <Typography
                    display={{ md: "none" }}
                    component={"td"}
                    variant="subtitle1"
                    fontWeight={600}
                    flexBasis="100%"
                  >
                    Potencia actual
                  </Typography>
  
                  {updateNewPowersColumns.map(
                    ({
                      id,
                      backgroundColor,
                      color,
                      width,
                      renderValue,
                      isUpdateField,
                      flexBasis,
                    }) => {
                      const cellValue = renderValue
                        ? renderValue(indexNumber)
                        : `${hiredPower} kW`;
                      const newPowersKey = `p${indexNumber + 1}`;
                      const isNewPowersError =
                        newPowersBlur[newPowersKey] &&
                        !!newPowers[newPowersKey]?.length &&
                        +newPowers[`p${indexNumber}`] > +newPowers[newPowersKey];
  
                      return (
                        <TableCell
                          key={id}
                          align={"center"}
                          width={width}
                          sx={{
                            backgroundColor,
                            borderRadius: "4px",
                            color,
                            position: "relative",
                            flexBasis: { xs: flexBasis, md: "auto" },
                            flexWrap: { xs: "wrap", md: "nowrap" },
                            gap: { xs: "15px", md: "" },
                            height: { xs: "fit-content", md: "100%" },
                          }}
                        >
                          {isUpdateField ? (
                            <>
                              <Stack
                                width="100%"
                                height="100%"
                                component={"span"}
                              >
                                <Typography
                                  display={{ xs: "flex", md: "none" }}
                                  variant="subtitle1"
                                  fontWeight={600}
                                  flexBasis="100%"
                                  paddingBottom="1rem"
                                  paddingTop="1rem"
                                >
                                  Nueva potencia
                                  <span style={{ color: "red" }}> *</span>
                                </Typography>
                                <FormControl
                                  variant="outlined"
                                  component={"span"}
                                >
                                  <OutlinedInput
                                    type="number"
                                    error={isNewPowersError}
                                    value={newPowers[newPowersKey] || ""}
                                    disabled={
                                      !!!indexNumber
                                        ? false
                                        : checkIsDisabled(indexNumber)
                                    }
                                    onChange={({ target: { value } }) =>
                                      setNewPowers((prevNewPowers) => ({
                                        ...prevNewPowers,
                                        [newPowersKey]: value,
                                      }))
                                    }
                                    onBlur={() => {
                                      setNewPowersBlur((prevNewPowersBlur) => ({
                                        ...prevNewPowersBlur,
                                        [newPowersKey]: true,
                                      }));
                                    }}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        kW
                                      </InputAdornment>
                                    }
                                    inputProps={{
                                      type: "number",
                                      pattern: "^-?\\d+(?:[.,]\\d{3})*$",
                                      style: {
                                        height: "100%",
                                        boxSizing: "border-box",
                                      },
                                    }}
                                  />
                                </FormControl>
                                {isNewPowersError && (
                                  <FormHelperText error>
                                    <span className="t-icon-CGU" />{" "}
                                    {`P${
                                      indexNumber + 1
                                    } debe ser mayor a P${indexNumber}`}
                                  </FormHelperText>
                                )}
                              </Stack>
                            </>
                          ) : (
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              marginY="1.5rem"
                            >
                              {cellValue}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    }
                  )}
                </TableRow>
              )
            )}
          </TableBody>
        )}
        {!isLoading && !!!powers.length && (
          <EmptyStateComponent text={"No hay resultados"} height="300px" />
        )}
      </Table>
    </TableContainer>
  );
};

export default ManagementsUpdateNewPowersTable;
